import React, { FC, useCallback, useMemo, useState } from 'react';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import SegmentedSelector from '@buyside/components/presentation/SegmentedSelector/segmentedSelector';

import useMakeStripeCheckout from '@buyside/util/hooks/useMakeStripeCheckout';

import { PaymentPlanInfo, IntervalType } from '@interfaces/payment/plans';

import styles from './subscribeCardStyles.scss';

const SubscribeCard: FC<PaymentPlanInfo> = ({ prices, sellingPoints, title }) => {
  const [selectedInterval, setSelectedInterval] = useState<IntervalType>(prices[0].interval);

  const { price, interval, stripePlan } = useMemo(
    () => prices.find(price => price.interval === selectedInterval) || prices[0],
    [prices, selectedInterval]
  );

  const { onSubmit, loading, error } = useMakeStripeCheckout(stripePlan);

  if (error) throw error;

  const generateIntervalLabel = useCallback((interval: IntervalType, intervalBadge?: string) => {
    return (
      <div className={styles.intervalLabel}>
        <span>{interval}</span>
        {intervalBadge ? <span className={styles.intervalLabelBadge}>{intervalBadge}</span> : null}
      </div>
    );
  }, []);

  return (
    <div className={styles.subscribeCard}>
      <div className={styles.subscribeCardTitle}>
        <h4>
          <strong>{title}</strong>
        </h4>
        {prices.length > 1 ? (
          <SegmentedSelector
            size="sm"
            onChange={interval => setSelectedInterval(interval)}
            options={prices.map(({ interval, intervalBadge }) => ({
              value: interval,
              label: generateIntervalLabel(interval, intervalBadge)
            }))}
            selected={selectedInterval}
          />
        ) : null}
      </div>
      <h2 className={styles.subscribeCardPrice} style={{ marginTop: 0 }}>
        <strong>${price.toLocaleString()}</strong> <span>{interval}</span>
      </h2>
      {sellingPoints.map((reason: { [key: string]: string[] }) => {
        const header = Object.keys(reason).toString();

        return (
          <div className={styles.subscribeCardSellingPoints} key={header}>
            <h4>
              <strong>{header}</strong>
            </h4>
            <ul className={styles.subscribeCardSellingPointsList}>
              {reason[header].map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        );
      })}
      <Button
        isLoading={loading}
        onClick={(): void => onSubmit()}
        text={`Choose This${interval === 'Per Bundle' ? ' Package' : ' Plan'}`}
        type={BUTTON_TYPES.PRIMARY}
      />
    </div>
  );
};

export default SubscribeCard;
